import { useEffect, useState } from 'react'
import { useAvatarBlob } from '../api/hooks'
import { Monogram } from './Monogram'
import { TOKENS } from './ui/dashboard-primitives'

/**
 * `<img src="/api/profile/avatar">` cannot work: the bearer token lives in
 * localStorage and is attached by the fetch wrappers, so a plain <img> sends
 * no Authorization header and gets a 401. Fetch the blob and render an object
 * URL instead -- and revoke it on unmount, or every avatar render leaks for
 * the life of the session.
 *
 * A 404 is the NORMAL state, not an error: it means no avatar, which means
 * monogram, which is the default (D10).
 */

/** Same ring every avatar in the app carries -- see Monogram.tsx. */
const CREAM_HAIRLINE = TOKENS.creamBorder

/**
 * Turns a Blob into an object URL for the lifetime of that Blob, revoking it
 * the instant it stops being current: when a new Blob replaces it, and on
 * unmount. The effect's own cleanup carries both cases -- it runs before the
 * next effect body (new Blob arrives) and on teardown (unmount) alike -- so
 * a URL is never revoked while it is still the one rendered, and never
 * created without a matching revoke.
 */
function useObjectUrl(blob: Blob | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)

  // `URL.createObjectURL`/`revokeObjectURL` is a real external-system sync,
  // not derivable render-time state: the object URL is a browser resource
  // with its own lifecycle, and only an effect can pair its creation with
  // the matching revoke on the next blob or on unmount.
  useEffect(() => {
    if (!blob) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(blob)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [blob])

  return url
}

interface AvatarProps {
  first?: string | null
  last?: string | null
  userId: number
  /** Diameter in px. Runtime-controlled, so this goes through inline style,
   * never a Tailwind class -- JIT cannot scan a template-literal class. */
  size: number
}

/**
 * Renders the user's uploaded photo when one exists, and the monogram
 * otherwise -- while loading, on a 404 (no avatar), and on a genuine fetch
 * error alike. The monogram is never a spinner-then-photo placeholder; it IS
 * the identity until a photo blob actually arrives (D10).
 */
export function Avatar({ first, last, userId, size }: AvatarProps) {
  const { data: blob } = useAvatarBlob(userId)
  const url = useObjectUrl(blob)

  if (!url) {
    return <Monogram first={first} last={last} userId={userId} size={size} />
  }

  return (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      decoding="async"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        boxShadow: `inset 0 0 0 1px ${CREAM_HAIRLINE}`,
        flexShrink: 0,
      }}
    />
  )
}
